import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Avatar,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Verified as VerifiedIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { UserListItem } from '../../types/user';

/**
 * Props for ApprovalCard component
 */
interface ApprovalCardProps {
  user: UserListItem;
  onApprove: (userId: string) => void;
  onReject: (userId: string) => void;
  loading?: boolean;
}

/**
 * ApprovalCard Component
 * Displays a pending user registration with approve/reject actions
 *
 * Features:
 * - User information (name, email, registration date)
 * - Email verification status
 * - Approve and reject buttons
 * - Loading states during action processing
 */
const ApprovalCard: React.FC<ApprovalCardProps> = ({
  user,
  onApprove,
  onReject,
  loading = false,
}) => {
  /**
   * Get user initials for avatar
   */
  const getInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Handle approve click
   */
  const handleApprove = () => {
    onApprove(user.uid);
  };

  /**
   * Handle reject click
   */
  const handleReject = () => {
    onReject(user.uid);
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* User Avatar and Basic Info */}
        <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: 'warning.main',
              fontSize: '1.5rem',
            }}
          >
            {getInitials(user.displayName)}
          </Avatar>

          <Box flexGrow={1}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
              <Typography variant="h6" fontWeight={600}>
                {user.displayName}
              </Typography>
              {user.isEmailVerified && (
                <Chip
                  icon={<VerifiedIcon />}
                  label="Verified"
                  size="small"
                  color="success"
                />
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={0.5} mb={1}>
              <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {user.email}
              </Typography>
            </Box>

            <Chip label="Pending Approval" size="small" color="warning" />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Registration Details */}
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              Registered:
            </Typography>
            <Typography variant="caption" fontWeight={600}>
              {user.createdAt ? format(user.createdAt, 'MMM d, yyyy h:mm a') : 'Unknown'}
            </Typography>
          </Box>

          {!user.isEmailVerified && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'warning.light',
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="warning.dark" fontWeight={600}>
                ⚠️ Email not verified
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>

      {/* Action Buttons */}
      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircleIcon />}
          onClick={handleApprove}
          disabled={loading}
          fullWidth
          sx={{ mr: 1 }}
        >
          Approve
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={handleReject}
          disabled={loading}
          fullWidth
        >
          Reject
        </Button>
      </CardActions>
    </Card>
  );
};

export default ApprovalCard;
